import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AvailabilityGrid from "./AvailabilityGrid";
import ReservationLedger from "./ReservationLedger";
import ESMMMapping from "./ESMMMapping";
import EddUpload from "./EddUpload";

import { AVAILABILITY_TABS } from "constants/availabilityConfig";
import { checkHasEddMasterAccess } from "utils/eddUtils";

// TODO: uncomment JITSellerPortal when backend is ready
// import JITSellerPortal from "./JITSellerPortal";

const TAB_COMPONENTS = {
  AVAILABILITY_DASHBOARD: <AvailabilityGrid />,
  DERIVED_INVENTORY: <ESMMMapping />,
  RESERVATION_LEDGER: <ReservationLedger />,
  EDD_MASTER: <EddUpload />,
};

const Availability = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userData);

  const hasEddMasterAccess = useMemo(
    () => checkHasEddMasterAccess(userData),
    [userData],
  );

  const visibleTabs = useMemo(
    () =>
      AVAILABILITY_TABS.filter((tab) => {
        if (tab.key === "EDD_MASTER") {
          return hasEddMasterAccess;
        }
        return true;
      }),
    [hasEddMasterAccess],
  );

  const [activeTab, setActiveTab] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState([0]);

  useEffect(() => {
    if (activeTab >= visibleTabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, visibleTabs.length]);

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    if (!visitedTabs.includes(v)) {
      setVisitedTabs((prev) => [...prev, v]);
    }
  };

  const safeActiveTab = activeTab < visibleTabs.length ? activeTab : 0;

  return (
    <Box>
      <Box display="flex" alignItems="flex-end" sx={{ mb: 3 }}>
        <IconButton size="small" onClick={() => navigate(-1)} sx={{ mr: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" noWrap>
          Availability
        </Typography>
      </Box>

      <Box
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 10,
          backgroundColor: theme.palette.background.default,
          pt: 1,
        }}
      >
        <Tabs
          value={safeActiveTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.accent.main,
              height: 4,
              borderRadius: "4px 4px 0 0",
            },
            minHeight: 48,
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.key || index}
              label={tab.label}
              sx={{
                textTransform: "none",
                minWidth: "auto",
                px: 3,
                fontWeight: 600,
                "&.Mui-selected": { color: theme.palette.accent.main },
                "&:hover": { color: theme.palette.accent.main, opacity: 1 },
              }}
            />
          ))}
        </Tabs>
        <Divider />
      </Box>

      <Box>
        {visibleTabs.map((tab, index) => {
          if (!visitedTabs.includes(index)) return null;
          return (
            <Box
              key={tab.key || index}
              sx={{ display: safeActiveTab === index ? "block" : "none" }}
            >
              {TAB_COMPONENTS[tab.key]}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Availability;